import { DEBOUNCE_TIME_MS } from "@/utils/utils";
import { Order_By } from "gql/api";
import type { ComputedRef, Ref, UnwrapRef } from "vue";
import {
  inject,
  reactive,
  computed,
  ref,
  watch,
  watchEffect,
  nextTick
} from "vue";
import type {
  BaseEquipment,
  EquipmentTableColumn,
  UseBaseEquipment
} from "./equipmentTypes";
import { useMutationNotification } from "@/composables/logger/mutationNotification";
import type { AnyVariables, OperationResult } from "@urql/core";
import Fuse from "fuse.js";
import {
  scenarioKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { watchDebounced } from "@vueuse/core";
import {
  KUWAIT_OIL_COMPANY_ID,
  ECOPETROL_IDS
} from "@/composables/auth/useUserInfo";

import { v4 } from "uuid";

type MutationResultPromise = Promise<OperationResult<unknown, AnyVariables>>;

export function filterEquipmentRowsForSizingCustomer<T extends BaseEquipment>(
  rows: T[],
  customerErpId: string | null | undefined,
  hasSizingContext: boolean
): T[] {
  if (customerErpId === KUWAIT_OIL_COMPANY_ID) {
    return rows.filter(
      row =>
        Boolean(row.include_koc_timestamp) || Boolean(row.only_koc_timestamp)
    );
  }
  if (customerErpId && ECOPETROL_IDS.includes(customerErpId)) {
    return rows.filter(
      row =>
        Boolean(row.include_ecopetrol_timestamp) ||
        Boolean(row.only_ecopetrol_timestamp)
    );
  }
  if (!hasSizingContext) return rows;
  return rows.filter(
    row => !row.only_ecopetrol_timestamp && !row.only_koc_timestamp
  );
}

export function useEquipment<T extends BaseEquipment>(
  isAdmin: boolean
): UseBaseEquipment<T> {
  const scenario = inject(scenarioKey, undefined);
  const scenarioId = computed<string>(() => scenario?.value?.id || "");

  const showInsertForm = ref(false);
  const enableRestore = computed(
    () => showDeleted.value && flagged.value.length > 0
  );
  const enableAdd = computed(() => {
    if (showInsertForm.value) {
      return false;
    }

    if (editingRows.value.length) {
      return false;
    }

    if (showDeleted.value) {
      return false;
    }

    return true;
  });
  const showUpdateForm = ref(true);
  const isSubmitting = ref(false);
  const isEditingAll = ref(false);

  const flagged = ref([]) as Ref<T[]>;
  const disableRestoreDelete = computed(() => flagged.value.length === 0);
  const duplicatedEquipmentId = ref<string>();
  const disableDuplicate = computed(
    () => flagged.value.length !== 1 || !!firstRow.value
  );

  const expandedRows = ref<{ id: string }[] | { [id: string]: boolean }>([]);
  const editingRows = ref([]) as Ref<T[]>;

  const itemsPerPage = ref(20);
  const itemsPageNum = ref(0);
  const showDeleted = ref(false);
  const isAdvanced = ref(false);
  const sortField = ref([{ updated_at: Order_By.Desc }]);
  const firstRow = ref<T | undefined>();

  const { registerResultToast } = useMutationNotification();

  function defineVisibleColumns(
    columns: EquipmentTableColumn<T>[]
  ): EquipmentTableColumn<T>[] {
    // #2491 Hides Y Tool Columns in Admin view
    return columns.filter(
      ({ adminOnly, hidden }) => !hidden && (isAdmin || !adminOnly)
    );
  }

  function handlePageChanged(e: { page: number; rows: number }): void {
    itemsPageNum.value = e.page;
    itemsPerPage.value = e.rows;
  }

  function handleEquipmentCancelled(): void {
    showInsertForm.value = false;
  }

  function handleRowCreate(): void {
    showInsertForm.value = true;
  }

  function handleDuplicateRow(): void {
    showInsertForm.value = true;
    const newRow = { ...flagged.value[0], id: v4(), created_at: "now()" };
    duplicatedEquipmentId.value = flagged.value[0].id;
    nextTick(() => {
      firstRow.value = newRow;
      editingRows.value = [newRow];
      flagged.value = [];
    });
  }

  function rowClass(data: { id: string }): "expanded" | undefined {
    // after updating primevue, it looks like their datatable is changing ecxpandedRows to an object when you bind it as v-model, adding redundancy incase they change it back
    const arrayOfIds: string[] = Array.isArray(expandedRows.value)
      ? expandedRows.value.map(r => (typeof r === "string" ? r : r?.id))
      : Object.keys(expandedRows.value);

    if (arrayOfIds.includes(data.id)) {
      return "expanded";
    }
  }

  function defineFilterData(obj: {
    title: string;
    columns: EquipmentTableColumn<T>[];
    allRows: ComputedRef<T[] | undefined>;
    globalSearchFields: (keyof T)[];
  }) {
    const { title, columns, allRows, globalSearchFields } = obj;

    const sessionStorageKey = `${title}-table-state-key-${
      isAdmin ? "admin" : "scenario"
    }-${scenarioId.value}`;

    const keys = columns
      .filter(col => col.searchable)
      .map(col => col.field as string);

    const initialSearchValues = sessionStorage.getItem(
      `${sessionStorageKey}-inputs`
    );

    // fields that v-model to the input text boxes
    const searchInputFields: Record<string, string> = reactive(
      initialSearchValues
        ? JSON.parse(initialSearchValues)
        : {
            global: "",
            ...keys.reduce((obj, key) => ({ ...obj, [key]: "" }), {})
          }
    );

    // filter list of results
    const filteredRows = ref<Fuse.FuseResult<T>[]>([]);

    const fuseOptions: Fuse.IFuseOptions<T> = {
      keys,
      findAllMatches: true,
      threshold: 0.4 // <- Change sensitivity of fuzzy search
    };

    const fuseIndex = computed(() =>
      Fuse.createIndex(keys, allRows.value || [])
    );

    // initialize fuse instance with dataset, options and index
    // computed value in order to reinitialize if data changes
    const fuse = computed(
      () => new Fuse(allRows.value || [], fuseOptions, fuseIndex.value)
    );

    function executeFilter() {
      const searchExpression: Fuse.Expression = { $and: [] };
      const { global, ...fields } = searchInputFields;
      if (global) {
        searchExpression.$and?.push({
          $or: globalSearchFields.map(field => ({
            [field]: searchInputFields.global
          }))
        });
      }

      for (const field in fields) {
        const searchValue = fields[field];
        if (searchValue) {
          searchExpression.$and?.push({ [field]: searchValue });
        }
      }

      if (searchExpression.$and && searchExpression.$and.length > 0) {
        filteredRows.value = fuse.value.search(searchExpression);
      } else {
        filteredRows.value = (allRows.value || []).map(row => ({
          item: row as UnwrapRef<T>,
          refIndex: 0
        }));
      }

      // save filter values to session storage
      sessionStorage.setItem(
        `${sessionStorageKey}-inputs`,
        JSON.stringify(searchInputFields)
      );
    }

    function clearFilters() {
      for (const field in searchInputFields) {
        searchInputFields[field] = "";
      }
      executeFilter();
    }

    // rerun filters if allRows gets new data
    watch(allRows, (newAllRows, oldAllRows) => {
      if (JSON.stringify(newAllRows) !== JSON.stringify(oldAllRows)) {
        executeFilter();
      }
    });

    // run first time the table is loaded
    executeFilter();

    // run the filter when anything search field is changed
    // but debounce input typing
    watchDebounced(searchInputFields, executeFilter, {
      debounce: DEBOUNCE_TIME_MS
    });

    const sizingForm = inject(sizingFormKey, null);
    const customerEligibleRows = computed(() =>
      filterEquipmentRowsForSizingCustomer(
        allRows.value || [],
        sizingForm?.data.value.customer?.customer_erp_id,
        Boolean(sizingForm)
      )
    );

    return {
      searchInputFields,
      executeFilter,
      clearFilters,
      sessionStorageKey,
      customerEligibleRows,
      filteredRows: computed(() => {
        const rows = filteredRows.value
          .map(row => row.item)
          .filter(row => showDeleted.value === Boolean(row.deleted_at));
        return filterEquipmentRowsForSizingCustomer(
          rows,
          sizingForm?.data.value.customer?.customer_erp_id,
          Boolean(sizingForm)
        );
      })
    };
  }

  function defineEmptyRow(createEmptyRow: () => Partial<T>) {
    watchEffect(() => {
      if (showInsertForm.value) {
        const newRow = createEmptyRow() as T;
        firstRow.value = newRow;
        editingRows.value = [newRow];
      } else {
        firstRow.value = undefined;
      }
    });
  }

  const defineMutationHandlers: UseBaseEquipment<T>["defineMutationHandlers"] =
    refetch => ({
      defineUpsert: (insertMutation, updateMutation) => async event => {
        delete event.newData.__typename;
        // Never want to explicity send author_id - hasura has a preset for it
        delete event.newData.author_id;

        for (const field in event.newData) {
          if (field.startsWith("r_")) {
            delete event.newData[field];
          }
        }

        isSubmitting.value = true;

        const object = {
          ...event.newData
        };

        if (object.created_at === "now()") {
          const result = insertMutation(object);
          registerResultToast(
            "Equipment Create",
            result as MutationResultPromise
          );
          await Promise.allSettled([result]);
        } else {
          const result = updateMutation(event.newData.id, object);
          registerResultToast(
            "Equipment Update",
            result as MutationResultPromise
          );
          await Promise.allSettled([result]);
        }
        isSubmitting.value = false;
        duplicatedEquipmentId.value = undefined;
        flagged.value = [];
        await refetch();

        // when saving new item remove first row
        if (event.newData.created_at === "now()") {
          showInsertForm.value = false;
          firstRow.value = undefined;
        }

        // remove this item from the editing rows list
        editingRows.value.splice(
          editingRows.value.findIndex(row => row.id === event.newData.id),
          1
        );
      },

      defineRestore: restoreMutation => async () => {
        const ids = flagged.value.map(s => s.id);

        const result = restoreMutation(ids);
        registerResultToast(
          "Equipment Restore",
          result as MutationResultPromise
        );
        await Promise.allSettled([result]);

        flagged.value = [];
        showDeleted.value = false;
        refetch();
      },

      defineDelete: deleteMutation => async () => {
        const ids = flagged.value.map(s => s.id);

        const result = deleteMutation(ids);
        registerResultToast(
          "Equipment Delete",
          result as MutationResultPromise
        );
        await Promise.allSettled([result]);

        flagged.value = [];
        refetch();
      }
    });

  // disallow rows to be both in editing mode and selected/flagged
  watch(editingRows, newEditingRows => {
    flagged.value = flagged.value.filter(row => !newEditingRows.includes(row));
  });

  return {
    showInsertForm,
    enableRestore,
    enableAdd,
    expandedRows,
    disableRestoreDelete,
    disableDuplicate,
    duplicatedEquipmentId,
    showUpdateForm,
    flagged,
    itemsPerPage,
    itemsPageNum,
    showDeleted,
    isAdvanced,
    sortField,
    firstRow,
    editingRows,
    isSubmitting,
    isEditingAll,
    defineMutationHandlers,
    handleEquipmentCancelled,
    handleRowCreate,
    handlePageChanged,
    handleDuplicateRow,
    rowClass,
    defineVisibleColumns,
    defineEmptyRow,
    defineFilterData
  };
}
